import { useTranslation } from "react-i18next";
import { Clock, RefreshCw, TriangleAlert, Wifi, WifiOff } from "lucide-react";
import { formatInstant } from "@/lib/format";
import { cn } from "@/lib/cn";

/**
 * The FOUR freshness signals, rendered with four DISTINCT labels (design §6.2 — the hardest lesson:
 * never reuse one timestamp for another):
 *
 *  - "mirror"        → per-feed Zoom data age from the ZPCD sync marker → "Last Update: {time}".
 *  - "mirror-health" → is the poller keeping the mirror current (a DIFFERENT question) → green/lagging.
 *  - "live"          → a sync-now live fetch instant → "Fetched live at {time}". ONLY from a sync-now ok.
 *  - "worklist"      → when `mv_account_360` (the worklist matview) last refreshed, from the /worklists
 *                      summary endpoint. NEVER the mirror timestamp — a paused/failed refresh must never
 *                      read as live, so the server computes `stale` and this badge turns amber when it is.
 *
 * The `kind` chooses the label; the same timestamp can NEVER feed two kinds (the caller passes the
 * right one). `nightly` selects the `mirrorNightly` copy variant — currently identical to `mirror`'s
 * "Last Update: {time}" in both locales (reserved for a distinct nightly qualifier if needed).
 */
export type FreshnessKind = "mirror" | "mirror-health" | "live" | "worklist";

interface MirrorProps {
  kind: "mirror";
  /** Per-feed ZPCD sync-marker instant (tz-aware ISO). */
  timestamp: string | null;
  /** Adds the nightly-sync qualifier. */
  nightly?: boolean;
  className?: string;
}

interface MirrorHealthProps {
  kind: "mirror-health";
  /** True = poller current; false = lagging. */
  healthy: boolean;
  /** When the poller-health was last evaluated (tz-aware ISO). */
  checkedAt?: string | null;
  className?: string;
}

interface LiveProps {
  kind: "live";
  /** The sync-now `live_at` instant — ONLY set on a sync-now ok response. */
  timestamp: string | null;
  className?: string;
}

interface WorklistProps {
  kind: "worklist";
  /** Last SUCCESSFUL mv_account_360 refresh (tz-aware ISO). NULL only pre-first-build. */
  refreshedAt: string | null;
  /** Server-computed: NULL refreshedAt, >45 min old, or the last refresh recorded status='error'. */
  stale: boolean;
  className?: string;
}

export type FreshnessBadgeProps = MirrorProps | MirrorHealthProps | LiveProps | WorklistProps;

export function FreshnessBadge(props: FreshnessBadgeProps) {
  const { t } = useTranslation();

  if (props.kind === "mirror") {
    const key = props.nightly ? "freshness.mirrorNightly" : "freshness.mirror";
    // (1) mirror = NEUTRAL — a quiet "data age" chip on a sunken fill.
    return (
      <Shell
        className={cn("bg-(--surface-sunken) text-(--text-muted) ring-(--border)", props.className)}
      >
        <Clock className="size-3 text-(--text-subtle)" aria-hidden />
        <span>{t(key, { time: formatInstant(props.timestamp) })}</span>
      </Shell>
    );
  }

  if (props.kind === "mirror-health") {
    // (2) mirror-health = GREEN (current) / AMBER (lagging) — a poller-health chip, distinct from age.
    const fill = props.healthy
      ? "bg-(--ok-subtle) text-(--ok) ring-(--ok-subtle)"
      : "bg-(--warn-subtle) text-(--warn) ring-(--warn-subtle)";
    return (
      <Shell className={cn(fill, props.className)}>
        {props.healthy ? (
          <Wifi className="size-3" aria-hidden />
        ) : (
          <WifiOff className="size-3" aria-hidden />
        )}
        <span>
          {props.healthy ? t("freshness.mirrorHealthOk") : t("freshness.mirrorHealthStale")}
        </span>
        {props.checkedAt && (
          <span className="opacity-70">
            · {t("freshness.mirrorHealthChecked", { time: formatInstant(props.checkedAt) })}
          </span>
        )}
      </Shell>
    );
  }

  if (props.kind === "live") {
    // (3) kind === "live" = ACCENT (indigo) — the sync-now "fetched just now" chip; only ever here.
    return (
      <Shell
        className={cn(
          "bg-(--brand-subtle) text-(--brand-strong) ring-(--brand-subtle)",
          props.className,
        )}
      >
        <RefreshCw className="size-3" aria-hidden />
        <span>{t("freshness.live", { time: formatInstant(props.timestamp) })}</span>
      </Shell>
    );
  }

  // (4) kind === "worklist" = NEUTRAL when fresh, AMBER past the 45-min threshold / on a recorded refresh
  // error / when refreshedAt is null — a paused or failed mv_account_360 refresh must never read as live.
  const time = formatInstant(props.refreshedAt);
  if (props.stale) {
    return (
      <Shell
        className={cn("bg-(--warn-subtle) text-(--warn) ring-(--warn-subtle)", props.className)}
      >
        <TriangleAlert className="size-3" aria-hidden />
        <span>{t("freshness.worklistStale", { time })}</span>
      </Shell>
    );
  }
  return (
    <Shell
      className={cn("bg-(--surface-sunken) text-(--text-muted) ring-(--border)", props.className)}
    >
      <Clock className="size-3 text-(--text-subtle)" aria-hidden />
      <span>{t("freshness.worklist", { time })}</span>
    </Shell>
  );
}

function Shell({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "tabular inline-flex w-fit items-center gap-1.5 rounded-full px-2 py-0.5 text-(length:--text-2xs) font-medium ring-1 ring-inset",
        className,
      )}
    >
      {children}
    </span>
  );
}
